import { useState, useEffect } from 'react'
import { FiTarget, FiPlus, FiEdit3 } from 'react-icons/fi'
import { motion, AnimatePresence } from 'framer-motion'
import { usePracticeTracker } from './usePracticeTracker'
import { useSession } from '../../app/useSession'
import { audioPlayer } from '../../lib/audio'
import Loading from '../../components/Loading'
import ErrorState from '../../components/ErrorState'
import { supabase } from '../../app/supabaseClient'

interface PracticeTrackerProps {
  lessonToolId: string
}

// Build the list of celebration URLs and check whether we should play
const buildCelebrationUrls = (lessonTool: any): string[] => {
  const urls: string[] = []
  const cfg = lessonTool?.tool_config ?? {}
  const tool = lessonTool?.tool ?? {} // values from `tools` table

  const play =
    cfg.play_celebration_sound ??
    tool.play_celebration_sound ??
    true
  if (!play) return urls

  const explicit =
    cfg.celebration_audio_url ||
    tool.celebration_audio_url
  if (explicit) urls.push(explicit)

  const rawBase =
    cfg.resources_path ||
    tool.resources_path
  if (rawBase) {
    const base = rawBase.endsWith('/') ? rawBase : `${rawBase}/`
    urls.push(`${base}celebration.mp3`)
    urls.push(`${base}1.mp3`)
  }

  return urls
}

// Always count from DB to avoid stale client state
const getFreshLogCount = async (practiceTrackerId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('practice_logs')
    .select('id', { count: 'exact', head: true })
    .eq('practice_tracker_id', practiceTrackerId)

  if (error) {
    console.warn('Failed to count practice_logs:', error)
    return 0
  }
  return count ?? 0
}

export default function PracticeTracker({ lessonToolId }: PracticeTrackerProps) {
  const { currentUserId } = useSession()
  const {
    tracker,
    lessonTool,
    logCount, // still used for UI, but celebration decisions use server count
    loading,
    error,
    isUpdating,
    isButtonDisabled,
    addPracticeLog,
    updateTrackerGoal,
    refetch
  } = usePracticeTracker(lessonToolId, currentUserId)

  const [isEditingGoal, setIsEditingGoal] = useState(false)
  const [goalInput, setGoalInput] = useState('')
  const [celebrationTriggered, setCelebrationTriggered] = useState(false)

  // Keyboard shortcuts (call our click handler so server-count logic runs)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return

      switch (e.key) {
        case ' ':
        case 'Enter':
          e.preventDefault()
          if (!isButtonDisabled && tracker) {
            handlePracticeClick()
          }
          break
        case 'g':
        case 'G':
          e.preventDefault()
          if (tracker && !isEditingGoal) {
            setIsEditingGoal(true)
            setGoalInput(tracker.goal_sessions.toString())
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isButtonDisabled, tracker, isEditingGoal])

  // If user raises the goal above server-count, allow celebration again
  useEffect(() => {
    let cancelled = false
    const maybeReset = async () => {
      if (!tracker) return
      const freshCount = await getFreshLogCount(tracker.id)
      if (!cancelled && tracker.goal_sessions > freshCount && celebrationTriggered) {
        setCelebrationTriggered(false)
      }
    }
    maybeReset()
    return () => { cancelled = true }
  }, [tracker?.goal_sessions, celebrationTriggered, tracker?.id])

  // Fallback: if page loads already at/over goal, try to celebrate (may be blocked by autoplay)
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!tracker || !lessonTool || celebrationTriggered) return
      const freshCount = await getFreshLogCount(tracker.id)
      const isGoalReached = freshCount >= tracker.goal_sessions && freshCount > 0
      if (!isGoalReached || cancelled) return

      setCelebrationTriggered(true)
      const urls = buildCelebrationUrls(lessonTool)
      if (urls.length === 0) return

      audioPlayer.playSound(urls).catch((e: unknown) => {
        console.warn('celebration fallback blocked or failed:', e)
      })
    }
    run()
    return () => { cancelled = true }
  }, [tracker, lessonTool, celebrationTriggered])

  const handleGoalSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const newGoal = parseInt(goalInput)
    if (newGoal > 0 && newGoal !== tracker?.goal_sessions) {
      updateTrackerGoal(newGoal)
    }
    setIsEditingGoal(false)
  }

  // Play correct sound every click; then check SERVER count to decide celebration
  const handlePracticeClick = async () => {
    if (!tracker || !lessonTool) return

    // 1) Correct sound on every click (inside user gesture)
    const correctUrl =
      'https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/correct.mp3'
    try {
      await audioPlayer.playSound([correctUrl])
    } catch (err) {
      console.warn('correct sound failed:', err)
    }

    // 2) Check server count BEFORE we write a new log; decide if this click reaches the goal
    let freshCount = await getFreshLogCount(tracker.id)
    const willReachGoal = freshCount < tracker.goal_sessions && freshCount + 1 >= tracker.goal_sessions

    // 3) If this click reaches goal, play celebration (still inside user gesture)
    if (willReachGoal && !celebrationTriggered) {
      const urls = buildCelebrationUrls(lessonTool)
      if (urls.length > 0) {
        try {
          await audioPlayer.playSound(urls)
          setCelebrationTriggered(true)
        } catch (err) {
          console.warn('celebration sound failed:', err)
        }
      } else {
        setCelebrationTriggered(true)
      }
    }

    // 4) Now record the log
    await addPracticeLog()

    // 5) Optional: quickly re-check server count to keep the reset logic snappy
    // (useful if someone changes the goal immediately after)
    freshCount = await getFreshLogCount(tracker.id)
    if (tracker.goal_sessions > freshCount && celebrationTriggered) {
      setCelebrationTriggered(false)
    }
  }

  if (loading) {
    return <Loading message="Loading practice tracker..." />
  }

  if (error) {
    return (
      <ErrorState
        title="Unable to load tracker"
        message={error}
        action={{
          label: 'Try Again',
          onClick: refetch
        }}
      />
    )
  }

  if (!tracker || !lessonTool) {
    return (
      <ErrorState
        title="Tracker not found"
        message="The practice tracker could not be found or you don't have access to it."
        action={{
          label: 'Refresh',
          onClick: refetch
        }}
      />
    )
  }

  const progress = Math.min((logCount / tracker.goal_sessions) * 100, 100)
  const isGoalReachedUI = logCount >= tracker.goal_sessions
  const title = lessonTool.tool_config?.title || 'Practice Tracker'

  return (
    <div className="w-full max-w-md mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 relative"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <FiTarget className="h-6 w-6 text-blue-600" />
            <h1 className="text-xl font-bold text-gray-900">{title}</h1>
          </div>
        </div>

        {/* Progress Display */}
        <div className="text-center mb-6">
          <motion.div
            key={logCount}
            initial={{ scale: 1 }}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 0.3 }}
            className="mb-4"
          >
            <span className="text-4xl font-bold text-gray-900">
              {logCount}
            </span>
            <span className="text-2xl text-gray-500 mx-2">/</span>
            {isEditingGoal ? (
              <form onSubmit={handleGoalSubmit} className="inline-block">
                <input
                  type="number"
                  value={goalInput}
                  onChange={(e) => setGoalInput(e.target.value)}
                  onBlur={() => setIsEditingGoal(false)}
                  className="text-2xl font-semibold text-blue-600 w-16 text-center border-b-2 border-blue-300 focus:border-blue-500 focus:outline-none bg-transparent"
                  min="1"
                  max="999"
                  autoFocus
                />
              </form>
            ) : (
              <button
                onClick={() => {
                  setIsEditingGoal(true)
                  setGoalInput(tracker.goal_sessions.toString())
                }}
                className="text-2xl font-semibold text-blue-600 hover:text-blue-700 focus-ring rounded px-1"
                title="Click to edit goal (or press 'g')"
              >
                {tracker.goal_sessions}
                <FiEdit3 className="inline h-4 w-4 ml-1 opacity-50" />
              </button>
            )}
          </motion.div>

          <p className="text-gray-600 mb-4">
            {isGoalReachedUI ? '🎉 Goal reached!' : 'practice sessions'}
          </p>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <motion.div
              className={`h-3 rounded-full ${
                isGoalReachedUI ? 'bg-green-500' : 'bg-blue-500'
              }`}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Practice Button */}
        <div className="text-center mb-4">
          <motion.button
            onClick={handlePracticeClick}
            disabled={isButtonDisabled || isUpdating}
            whileTap={{ scale: 0.98 }}
            className={`
              w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all focus-ring
              ${isButtonDisabled || isUpdating
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : isGoalReachedUI
                ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
              }
            `}
          >
            <div className="flex items-center justify-center gap-2">
              {isUpdating ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <FiPlus className="h-5 w-5" />
              )}
              {isGoalReachedUI ? 'Keep practicing!' : 'I practiced'}
            </div>
          </motion.button>
        </div>

        {/* Keyboard Shortcuts */}
        <div className="text-xs text-gray-500 text-center space-y-1">
          <div>Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">Space</kbd> or <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">Enter</kbd> to log practice</div>
          <div>Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">G</kbd> to edit goal</div>
        </div>

        {/* Celebration Animation */}
        <AnimatePresence>
          {isGoalReachedUI && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <div className="text-6xl animate-bounce-gentle">🎉</div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}